import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Edit, Search, Filter, Users, Camera, Bell, 
  Settings, X, Save, RefreshCw, AlertCircle, ChevronLeft, ChevronRight
} from "lucide-react";
import { Card } from "../ui/card";
import { Button } from "../ui/Button";
import { Badge } from "../ui/badge";
import { Alert, AlertDescription } from "../ui/alert";
import { User } from "../../modules/shared/model/AuthInterfaces";
import { getAllUsers } from "../../redux/Actions/AdminAction";
import { UpdateUserProfile, getUserById } from "../../redux/Actions/AuthAction";
import { RootState, useAppDispatch } from "../../redux/store";
import { RoleEnum } from "../../modules";
import UserDropdown from "../ui/UserDropdown";
import { webSocketService } from "../../services/websocketService";

interface UserPermissionsProps {
  className?: string;
  showHeader?: boolean;
  showFilters?: boolean;
  showActions?: boolean;
  onUserSelect?: (user: User) => void;
}

const UserPermissions: React.FC<UserPermissionsProps> = ({
  className = "",
  showHeader = true,
  showFilters = true,
  showActions = true,
  onUserSelect
}) => {
  const dispatch = useAppDispatch();
  const { allUsers: users, error: usersError } = useSelector((state: RootState) => state.admin);
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const usersLoading = false; // We'll handle loading state differently

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState<string>("");
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(10);

  // Stable roles array to prevent infinite re-renders in UserDropdown
  const userRoles = useMemo(() => [
    { id: RoleEnum.client },
    { id: RoleEnum.professional },
    { id: RoleEnum.internationl_professional }
  ], []);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500); // 500ms debounce

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch users when filters change
  useEffect(() => {
    dispatch(getAllUsers({ 
      page: currentPage, 
      limit: limit,
      search: debouncedSearchQuery,
      roles: roleFilter ? [{ id: Number(roleFilter) }] : undefined
    }));
  }, [dispatch, currentPage, limit, debouncedSearchQuery, roleFilter]);

  // Handle search
  const handleSearch = (query: string) => {
    setSearchQuery(query);
    setCurrentPage(1); // Reset to first page when search changes
  };

  // Handle filter changes
  const handleFilterChange = (value: string) => {
    setRoleFilter(value);
    setCurrentPage(1); // Reset to first page when filter changes
  };

  // Handle edit user
  const handleEditUser = (user: User) => {
    console.log("Editing user:", user);
    console.log("Camera permission:", user.cameraPermission);
    console.log("Enable notification:", user.enableNotification);
    setUpdateLoading(false);
    setEditingUser(user);
    setShowEditModal(true);
  };

  // Handle update user permissions
  const handleUpdateUserPermissions = async () => {
    if (!editingUser) return;

    setUpdateLoading(true);
    const editableUserId = String(editingUser.id);
    const pendingPermissions = {
      cameraPermission: editingUser.cameraPermission,
      enableNotification: editingUser.enableNotification,
      photo: editingUser.photo,
      cover: editingUser.cover,
    };

    try {
      console.log("Updating user with data:", pendingPermissions);

      const resultAction = await dispatch(UpdateUserProfile({ id: editableUserId, data: pendingPermissions }));

      if (!UpdateUserProfile.fulfilled.match(resultAction)) {
        throw new Error((resultAction as any)?.payload || "Failed to update user permissions");
      }

      const updatedUser = resultAction.payload as User | undefined;
      const broadcastUser = updatedUser ?? { ...editingUser, ...pendingPermissions };

      setShowEditModal(false);
      setEditingUser(null);

      if (currentUser?.id && String(currentUser.id) === editableUserId) {
        await dispatch(getUserById({ id: editableUserId, isShowLoader: false }));
      }

      if (webSocketService.getConnectionStatus()) {
        try {
          await webSocketService.sendMessage({
            receivers: [editableUserId],
            sender: currentUser?.id ? String(currentUser.id) : "",
            type: "user_permissions_updated",
            detail: {
              userId: editableUserId,
              cameraPermission: broadcastUser?.cameraPermission,
              enableNotification: broadcastUser?.enableNotification,
              updatedBy: currentUser?.id || null,
              timestamp: new Date().toISOString()
            }
          });
        } catch (socketError) {
          console.error("Failed to send permissions update via WebSocket:", socketError);
        }
      }

      dispatch(getAllUsers({ 
        page: currentPage, 
        limit: limit,
        search: debouncedSearchQuery,
        roles: roleFilter ? [{ id: Number(roleFilter) }] : undefined
      }));
      setUpdateLoading(false);
    } catch (error) {
      console.error("Failed to update user permissions:", error);
      setUpdateLoading(false);
    }

  };

  // Handle user click
  const handleUserClick = (user: User) => {
    if (onUserSelect) {
      onUserSelect(user);
    }
  };


  // User Role Dropdown Component - Responsive
  const UserRoleDropdown = () => (
    <select
      value={roleFilter}
      onChange={(e) => handleFilterChange(e.target.value)}
      className="w-full px-3 py-2 sm:py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
    >
      <option value="">All Roles</option>
      <option value={RoleEnum.client}>Client</option>
      <option value={RoleEnum.professional}>Professional</option>
      <option value={RoleEnum.internationl_professional}>International Professional</option>
    </select>
  );

  // Helper function to get user display name
  const getUserDisplayName = (user: User) => {
    if (!user) return "Unknown User";
    
    // If user has firstName and lastName
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    
    // Fallback
    return user.email || "Unknown User";
  };

  // User Card Component with responsive design
  const UserCard: React.FC<{ user: User }> = ({ user }) => (
    <Card 
      className="p-3 sm:p-4 lg:p-6 hover:shadow-md transition-shadow cursor-pointer border border-gray-200 hover:border-gray-300"
      onClick={() => handleUserClick(user)}
    >
      {/* Header Section - Responsive Layout */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 sm:gap-4 mb-4">
        {/* User Info */}
        <div className="flex-1 min-w-0">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-2">
            <h3 className="font-semibold text-base sm:text-lg text-gray-900 truncate">
              {getUserDisplayName(user)}
            </h3>
            <Badge 
              variant={undefined} 
              className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                Number(user.role?.id) === RoleEnum.client 
                  ? "bg-green-100 text-green-700"
                  : Number(user.role?.id) === RoleEnum.professional
                  ? "bg-blue-100 text-blue-700"
                  : "bg-purple-100 text-purple-700"
              }`}
            >
              {user.role?.id === "2" ? "Client" : user.role?.id === "4" ? "Professional" : user.role?.id === "5" ? "International Professional" : "Unknown"}
            </Badge>
          </div>
          <p className="text-sm text-gray-600 truncate">{user.email || "No email"}</p>
          {user.title && (
            <p className="text-sm text-gray-500 truncate">{user.title}</p>
          )}
        </div>
        
        {/* Action Buttons - Hidden on mobile, shown on tablet+ */}
        {showActions && (
          <div className="flex gap-2 self-end sm:self-start">
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 sm:h-9 sm:w-9"
              onClick={(e) => {
                e.stopPropagation();
                handleEditUser(user);
              }}
            >
              <Edit className="h-3 w-3 sm:h-4 sm:w-4" />
            </Button>
          </div>
        )}
      </div>

      {/* Permissions Grid - Responsive */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
        {/* Camera Access */}
        <div className="p-3 sm:p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Camera className="h-4 w-4 sm:h-5 sm:w-5 text-gray-500 flex-shrink-0" />
              <span className="text-sm font-medium text-gray-700">Camera Access</span>
            </div>
            <Badge 
              variant={undefined} 
              className={`px-2 py-1 text-xs font-medium ${
                user.cameraPermission === true
                  ? "bg-green-100 text-green-700" 
                  : "bg-red-100 text-red-700"
              }`}
            >
              {user.cameraPermission === true ? "Enabled" : "Disabled"}
            </Badge>
          </div>
        </div>
        
        {/* Notification Access */}
        <div className="p-3 sm:p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Bell className="h-4 w-4 sm:h-5 sm:w-5 text-gray-500 flex-shrink-0" />
              <span className="text-sm font-medium text-gray-700">Notifications</span>
            </div>
            <Badge 
              variant={undefined} 
              className={`px-2 py-1 text-xs font-medium ${
                user.enableNotification === true
                  ? "bg-green-100 text-green-700" 
                  : "bg-red-100 text-red-700"
              }`}
            >
              {user.enableNotification === true ? "Enabled" : "Disabled"}
            </Badge>
          </div>
        </div>
      </div>

      {/* Footer - Responsive */}
      <div className="mt-4 pt-3 sm:pt-4 border-t border-gray-200">
        <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-0 text-xs text-gray-500">
          <span>Created: {new Date(user.createdAt).toLocaleDateString()}</span>
          <span>Updated: {new Date(user.updatedAt).toLocaleDateString()}</span>
        </div>
      </div>
    </Card>
  );

  // Edit Modal Component
  const EditModal = () => {
    if (!editingUser) return null;

    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold">Edit User Permissions</h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setUpdateLoading(false);
                  setShowEditModal(false);
                }}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div className="p-6 space-y-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
                <p className="text-sm text-blue-700">
                  Editing permissions for {getUserDisplayName(editingUser)}
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Camera className="h-5 w-5 text-gray-700" />
                    <div>
                      <p className="font-medium">Camera Access</p>
                      <p className="text-sm text-gray-500">Allow video call features</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="cameraPermission"
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                      checked={editingUser.cameraPermission === true}
                      onChange={(e) => {
                        console.log("Camera permission changed to:", e.target.checked);
                        setEditingUser(prev => prev ? {
                          ...prev,
                          cameraPermission: e.target.checked
                        } : null);
                      }}
                    />
                    <label htmlFor="cameraPermission" className="text-sm font-medium text-gray-700">
                      {editingUser.cameraPermission === true ? "Enabled" : "Disabled"}
                    </label>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Bell className="h-5 w-5 text-gray-700" />
                    <div>
                      <p className="font-medium">Notification Access</p>
                      <p className="text-sm text-gray-500">Allow notifications</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="enableNotification"
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                      checked={editingUser.enableNotification === true}
                      onChange={(e) => {
                        console.log("Notification permission changed to:", e.target.checked);
                        setEditingUser(prev => prev ? {
                          ...prev,
                          enableNotification: e.target.checked
                        } : null);
                      }}
                    />
                    <label htmlFor="enableNotification" className="text-sm font-medium text-gray-700">
                      {editingUser.enableNotification === true ? "Enabled" : "Disabled"}
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="p-6 border-t flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => {
                setUpdateLoading(false);
                setShowEditModal(false);
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleUpdateUserPermissions} disabled={updateLoading}>
              {updateLoading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Save Changes
            </Button>
          </div>
        </div>
      </div>
    );
  };


  return (
    <div className={`space-y-6 ${className}`}>
      {showHeader && (
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">User Permissions</h1>
            <p className="text-gray-600 mt-1">Manage user permissions and access controls</p>
          </div>
        </div>
      )}

      {/* Stats Cards - Responsive */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <Card className="p-3 sm:p-4 hover:shadow-sm transition-shadow">
          <div className="flex justify-between items-start">
            <div className="flex-1 min-w-0">
              <p className="text-xs sm:text-sm text-gray-500 truncate">Total Users</p>
              <p className="text-xl sm:text-2xl font-semibold mt-1">{users?.totalCount || 0}</p>
            </div>
            <div className="p-2 bg-blue-100 rounded-lg flex-shrink-0 ml-2">
              <Users className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
            </div>
          </div>
        </Card>
        <Card className="p-3 sm:p-4 hover:shadow-sm transition-shadow">
          <div className="flex justify-between items-start">
            <div className="flex-1 min-w-0">
              <p className="text-xs sm:text-sm text-gray-500 truncate">Camera Enabled</p>
              <p className="text-xl sm:text-2xl font-semibold mt-1">
                {users?.data?.filter((u: User) => u.cameraPermission === true).length || 0}
              </p>
            </div>
            <div className="p-2 bg-green-100 rounded-lg flex-shrink-0 ml-2">
              <Camera className="h-4 w-4 sm:h-5 sm:w-5 text-green-600" />
            </div>
          </div>
        </Card>
        <Card className="p-3 sm:p-4 hover:shadow-sm transition-shadow">
          <div className="flex justify-between items-start">
            <div className="flex-1 min-w-0">
              <p className="text-xs sm:text-sm text-gray-500 truncate">Notifications Enabled</p>
              <p className="text-xl sm:text-2xl font-semibold mt-1">
                {users?.data?.filter((u: User) => u.enableNotification === true).length || 0}
              </p>
            </div>
            <div className="p-2 bg-yellow-100 rounded-lg flex-shrink-0 ml-2">
              <Bell className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-600" />
            </div>
          </div>
        </Card>
        <Card className="p-3 sm:p-4 hover:shadow-sm transition-shadow">
          <div className="flex justify-between items-start">
            <div className="flex-1 min-w-0">
              <p className="text-xs sm:text-sm text-gray-500 truncate">Active Users</p>
              <p className="text-xl sm:text-2xl font-semibold mt-1">{users?.data?.length || 0}</p>
            </div>
            <div className="p-2 bg-purple-100 rounded-lg flex-shrink-0 ml-2">
              <Settings className="h-4 w-4 sm:h-5 sm:w-5 text-purple-600" />
            </div>
          </div>
        </Card>
      </div>

      {/* Filters - Responsive */}
      {showFilters && (
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search users..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 sm:py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
            />
          </div>
          <div className="w-full sm:w-auto">
            <UserRoleDropdown />
          </div>
          <Button 
            variant="outline" 
            onClick={() => {
              setSearchQuery("");
              setDebouncedSearchQuery("");
              setRoleFilter("");
              setCurrentPage(1);
            }}
            className="w-full sm:w-auto flex items-center justify-center gap-2 py-2 sm:py-2.5"
          >
            <Filter className="h-4 w-4" />
            <span className="hidden sm:inline">Reset</span>
            <span className="sm:hidden">Reset Filters</span>
          </Button>
        </div>
      )}

      {/* Error Display */}
      {usersError && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {usersError}
            <Button 
              variant="ghost" 
              size="sm" 
              className="ml-2"
              onClick={() => {
                // Refresh users list
                dispatch(getAllUsers({ 
                  page: currentPage, 
                  limit: limit,
                  search: debouncedSearchQuery,
                  roles: roleFilter ? [{ id: Number(roleFilter) }] : undefined
                }));
              }}
            >
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Users Grid - Responsive */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
        {usersLoading ? (
          Array.from({ length: 6 }).map((_, index) => (
            <Card key={index} className="p-3 sm:p-4 lg:p-6">
              <div className="animate-pulse">
                <div className="h-4 sm:h-5 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2 mb-3 sm:mb-4"></div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div className="h-12 sm:h-16 bg-gray-200 rounded"></div>
                  <div className="h-12 sm:h-16 bg-gray-200 rounded"></div>
                </div>
              </div>
            </Card>
          ))
        ) : !users?.data || users.data.length === 0 ? (
          <div className="col-span-full text-center py-8 sm:py-12 px-4">
            <Users className="h-10 w-10 sm:h-12 sm:w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">No users found</h3>
            <p className="text-sm sm:text-base text-gray-500 mb-4">No users match your current filters.</p>
          </div>
        ) : (
          users.data.map((user: User) => (
            <UserCard key={user.id} user={user} />
          ))
        )}
      </div>

      {/* Pagination */}
      {users?.data && users.data.length > 0 && (
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 px-4 py-6 sm:py-8 mb-8 sm:mb-12 lg:mb-16">
          <div className="text-sm text-gray-600">
            Showing {((currentPage - 1) * limit) + 1} to {Math.min(currentPage * limit, users.totalCount || 0)} of {users.totalCount || 0} users
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(currentPage - 1)}
              className="flex items-center gap-1"
            >
              <ChevronLeft className="h-4 w-4" />
              <span className="hidden sm:inline">Previous</span>
            </Button>
            
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(5, Math.ceil((users.totalCount || 0) / limit)) }, (_, i) => {
                const pageNum = i + 1;
                const isActive = pageNum === currentPage;
                
                return (
                  <Button
                    key={pageNum}
                    variant={isActive ? "default" : "outline"}
                    size="sm"
                    onClick={() => setCurrentPage(pageNum)}
                    className={`w-8 h-8 p-0 ${isActive ? "bg-blue-600 text-white" : ""}`}
                  >
                    {pageNum}
                  </Button>
                );
              })}
            </div>
            
            <Button
              variant="outline"
              size="sm"
              disabled={!users.hasNextPage}
              onClick={() => setCurrentPage(currentPage + 1)}
              className="flex items-center gap-1"
            >
              <span className="hidden sm:inline">Next</span>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Modals */}
      {showEditModal && <EditModal />}
    </div>
  );
};

export default UserPermissions;
